import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listMetrcPackages, type MetrcPackage } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';

/**
 * Metrc packages — the state's record of physical inventory.
 *
 * A package is the unit track-and-trace reasons about: a tagged quantity of one
 * product, with a provenance chain back to the harvest. This lists the mirror
 * that Settings → Metrc syncs, so it shows every lot the licence holds —
 * including the ones nobody has assigned to a product yet, which is exactly the
 * queue this screen exists to work through.
 *
 * Assignment is what connects a lot to something sellable and puts its stock on
 * the shelf; it happens on the detail screen.
 *
 * @see docs/metrc.md
 */
export const Route = createFileRoute('/packages/')({
  component: PackagesPage,
});

interface PackageRow {
  id: string;
  label: string;
  item: string;
  strain: string;
  assigned: string | null;
  assignedImage: string | null;
  quantity: string;
  units: number | null;
  listType: string;
  expiresAt: string | null;
}

const column = dataTableColumnHelper<PackageRow>();

const columns = column.columns([
  column.accessor('label', {
    header: 'Metrc tag',
    cell: ({ getValue }) => (
      // 24 characters of state-issued identifier that people read off a
      // physical label, so it is monospaced and never truncated.
      <span className="font-mono text-xs">{getValue()}</span>
    ),
  }),

  column.accessor('item', {
    header: 'Metrc item',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate">{row.original.item || '—'}</div>
        {row.original.strain && (
          <div className="text-muted-foreground truncate text-xs">{row.original.strain}</div>
        )}
      </div>
    ),
  }),

  column.accessor('assigned', {
    header: 'Sold as',
    cell: ({ row }) => {
      const { assigned, assignedImage, units } = row.original;

      if (!assigned) {
        return <Badge variant="outline">Unassigned</Badge>;
      }

      return (
        <div className="flex min-w-0 items-center gap-2">
          {assignedImage ? (
            <img
              src={assignedImage}
              alt=""
              className="size-8 shrink-0 rounded border object-cover"
            />
          ) : (
            <div className="bg-muted size-8 shrink-0 rounded border" />
          )}
          <div className="min-w-0">
            <div className="truncate font-medium">{assigned}</div>
            <div className="text-muted-foreground text-xs">
              {units} unit{units === 1 ? '' : 's'} on hand
            </div>
          </div>
        </div>
      );
    },
  }),

  column.accessor('quantity', {
    header: 'Metrc quantity',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => <span>{getValue()}</span>,
  }),

  column.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row }) => (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/packages/$packageId" params={{ packageId: row.original.id }}>
          {row.original.assigned ? 'View' : 'Assign'}
        </Link>
      </Button>
    ),
  }),

  column.accessor('expiresAt', {
    header: 'Expires',
    meta: { className: 'text-right' },
    cell: ({ getValue }) => {
      const value = getValue();

      if (!value) return <span className="text-muted-foreground">—</span>;

      const date = new Date(value);
      const expired = date.getTime() <= Date.now();

      return (
        <span className={expired ? 'text-destructive font-medium' : ''}>
          {date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
        </span>
      );
    },
  }),
]);

function PackagesPage() {
  const { activeAccountId } = useAuth();
  const navigate = useNavigate();
  const accountId = activeAccountId ?? '';
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('all');

  const packages = useQuery({
    queryKey: ['metrc-packages', accountId, search, filter],
    queryFn: () =>
      listMetrcPackages(accountId, {
        search: search || undefined,
        assigned: filter === 'all' ? undefined : filter === 'assigned',
        limit: 200,
      }),
    enabled: Boolean(accountId),
    // Keeps the table on screen while a new search resolves, rather than
    // flashing an empty state between keystrokes.
    placeholderData: keepPreviousData,
  });

  const rows = useMemo<PackageRow[]>(
    () =>
      (packages.data?.data ?? []).map((pkg: MetrcPackage) => ({
        id: pkg.id,
        label: pkg.label,
        item: pkg.itemName ?? '',
        strain: pkg.strainName ?? '',
        assigned: pkg.assignment
          ? `${pkg.assignment.productName ?? 'Unknown'} · ${pkg.assignment.variantName ?? ''}`.trim()
          : null,
        assignedImage: pkg.assignment?.productImageUrl ?? null,
        units: pkg.assignment?.units ?? null,
        quantity: `${pkg.quantity ?? 0} ${pkg.unitOfMeasureName ?? ''}`.trim(),
        listType: pkg.listType ?? '',
        expiresAt: pkg.expirationDate,
      })),
    [packages.data],
  );

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Soonest expiry first: an expiring package is the one with a deadline.
      sorting: [{ id: 'expiresAt', desc: false }],
    },
  });

  const unassigned = rows.filter((row) => !row.assigned).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by tag or item…"
          className="max-w-xs"
          aria-label="Search packages"
        />

        {(['all', 'unassigned', 'assigned'] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={filter === value ? 'default' : 'outline'}
            onClick={() => setFilter(value)}
            className="capitalize"
          >
            {value}
            {value === 'unassigned' && unassigned > 0 ? ` (${unassigned})` : ''}
          </Button>
        ))}
      </div>

      {packages.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState>
          {search || filter !== 'all'
            ? 'No packages match that.'
            : 'No Metrc packages yet. Connect a facility and sync packages under Settings → Metrc.'}
        </EmptyState>
      ) : (
        <>
          <DataTable
            table={table}
            columnCount={columns.length}
            onRowActivate={(row) =>
              navigate({ to: '/packages/$packageId', params: { packageId: row.id } })
            }
          />
          <DataTablePagination table={table} total={packages.data?.total ?? rows.length} />
        </>
      )}
    </div>
  );
}
